import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import useBeforeUnload from 'hooks/useBeforeUnload';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import rubricService from 'services/rubric';
import { Button, Stack } from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getRubric, getWordCount, isValidRate, roundCriteriaOptionRates, sortCriteria } from 'utils/helper';
import { v4 as uuid } from 'uuid';

export const TABS = {
    FILE: 'file',
    TEXT: 'text',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { setNode } = useAppBar();
    const { course, setRubrics } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const isCancelled = useRef(false);
    const [tab, setTab] = useState(TABS.FILE);
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [value, setValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [rubricErrors, setRubricErrors] = useState({
        title: '',
        criteria: {},
    });
    const [hasClickedSubmitRubric, setHasClickedSubmitRubric] = useState(false);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('dialog.return-to-settings.title', 'Returning to Settings Will Interrupt Rubric Generation'),
            description: t(
                'dialog.return-to-settings.description',
                'If you proceed, the rubric will not be generated. Do you want to continue?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setIsLoading(false);
                isCancelled.current = true;
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const validateTitle = useCallback(() => {
        const error = result.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setRubricErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [result?.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        result.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setRubricErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [result?.criteria, t]);

    const onClickEditSettings = () => {
        setDialog({
            open: true,
            message: t('dialog.editing-settings-warning.title', 'Editing Settings Will Erase This Rubric'),
            description: t(
                'dialog.editing-settings-warning.description',
                'If you continue, you will need to generate a new rubric.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setResult(null);
                setRubricErrors({ title: '', criteria: {} });
                setHasClickedSubmitRubric(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickGenerate = useCallback(async () => {
        if (tab === TABS.FILE && (!files.length || Boolean(error))) {
            return message.error(t('rubric.create-existing-rubric.file.error', 'Please upload a file.'));
        }
        if (tab === TABS.TEXT && !Boolean(value)) {
            return message.error(t('rubric.create-existing-rubric.text.error', 'Please enter your rubric.'));
        }
        isCancelled.current = false;
        setIsLoading(true);
        const rubricSource = {
            [TABS.FILE]: {
                ...files?.[0]?.data,
            },
            [TABS.TEXT]: value,
        }[tab];
        try {
            const response = await getRubric({ type: tab, rubricSource, language: course?.language });
            const data = await response.json();
            if (isCancelled.current) return;
            const { result } = data;
            if (!result || !result.criteria.length || result.criteria.some(({ options }) => options?.length < 2)) {
                throw new Error(JSON.stringify(result));
            }
            setResult({
                title: result.title,
                criteria: result.criteria.map((criterion) => {
                    return {
                        ...criterion,
                        id: uuid(),
                        options: criterion.options
                            .sort((a, b) => {
                                const numA = Number(a.rate);
                                const numB = Number(b.rate);
                                if (a.rate === '') return 1;
                                if (b.rate === '') return -1;
                                if (!numA && !numB) return 0;
                                return numB - numA;
                            })
                            .map((option) => ({ ...option, id: uuid(), rate: option.rate.toString() })),
                    };
                }),
                sort: 'desc',
            });
        } catch (e) {
            console.error(e);
            message.error(
                t(
                    'rubric.create-ai-rubric.error.rubric-generation-failed',
                    'Rubric generation failed, please try again.'
                )
            );
        } finally {
            setIsLoading(false);
        }
    }, [course?.language, error, files, message, t, tab, value]);

    const onClickCreate = useCallback(() => {
        setHasClickedSubmitRubric(true);
        const hasAtLeastOneCriterion = result.criteria.length > 0;
        const isValid = validateTitle() && validateCriteria() && hasAtLeastOneCriterion;
        if (isValid) {
            setDialog({
                open: true,
                message: t('dialog.save-rubric-warning.title', 'Did You Verify All AI-Generated Information?'),
                description: t(
                    'dialog.save-rubric-warning.description',
                    'Before proceeding, please confirm that you have reviewed and verified all AI-generated information for accuracy and completeness.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.confirm', 'Confirm'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const timestamp = new Date().getTime();
                    const criteriaRoundedOptionRates = roundCriteriaOptionRates(result.criteria);
                    const sortedCriteria = sortCriteria(criteriaRoundedOptionRates, result.sort);
                    const rubric = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        rubric: { ...result, criteria: sortedCriteria },
                        user_id: auth.user_id,
                    };
                    setRubrics((current) => [...current, rubric]);
                    rubricService.createRubric(rubric);
                    history.push('/rubrics');
                    message.success(t('message.rubric-saved-success', 'Rubric was saved successfully.'));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            message.error(t('error.invalid-rubric', 'The rubric is not valid.'));
        }
    }, [
        auth.user_id,
        closeDialog,
        history,
        message,
        result,
        setDialog,
        setRubrics,
        t,
        validateCriteria,
        validateTitle,
    ]);

    useEffect(() => {
        if (isLoading) return;

        if (result) {
            setNode(
                <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                    <Button
                        color={'neutral'}
                        variant={'tertiary'}
                        onClick={() => {
                            setDialog({
                                open: true,
                                message: t(
                                    'dialog.return-rubric-generation-settings.title',
                                    'Return to Rubric Generation Settings?'
                                ),
                                description: t(
                                    'dialog.return-rubric-generation-settings.description',
                                    'If you exit, this generated rubric will be lost.'
                                ),
                                note: '',
                                variant: 'destructive',
                                actionButtonText: t('common:controls.exit', 'Exit'),
                                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                                onAction: () => {
                                    setResult(null);
                                    setRubricErrors({ title: '', criteria: {} });
                                    setHasClickedSubmitRubric(false);
                                    closeDialog();
                                },
                                onCancel: () => closeDialog(),
                                onClose: () => closeDialog(),
                            });
                        }}
                    >
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={onClickCreate}>{t('rubric.create-rubric', 'Create Rubric')}</Button>
                </Stack>
            );
        } else {
            setNode(
                <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                    <Button color={'neutral'} variant={'tertiary'} onClick={() => history.push('/rubrics/get-started')}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button
                        disabled={tab === TABS.FILE && Boolean(files.length) && files.every(({ loading }) => loading)}
                        onClick={onClickGenerate}
                    >
                        {t('rubric.generate-rubric', 'Generate Rubric')}
                    </Button>
                </Stack>
            );
        }

        return () => setNode(null);
    }, [closeDialog, files, history, isLoading, onClickCreate, onClickGenerate, result, setDialog, setNode, t, tab]);

    useEffect(() => {
        if (isLoading) {
            onBeforeNavigate((navigate) => {
                setDialog({
                    open: true,
                    message: t(
                        'dialog.navigating-away-rubric-generation.title',
                        'Navigating Away Will Interrupt Rubric Generation'
                    ),
                    description: t(
                        'dialog.navigating-away-rubric-generation.description',
                        'If you proceed, the rubric will not be generated. Do you want to continue?'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.continue', 'Continue'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        navigate();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            });
        }

        if (result) {
            onBeforeNavigate((navigate) => {
                setDialog({
                    open: true,
                    message: t('dialog.discard-rubric.title', 'Discard Rubric?'),
                    description: t(
                        'dialog.discard-rubric.description',
                        'If you exit, this generated rubric will be lost.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.continue', 'Continue'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => {
                        closeDialog();
                        navigate();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            });
        }

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, isLoading, onBeforeNavigate, result, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                tab,
                setTab,
                files,
                setFiles,
                error,
                setError,
                value,
                setValue,

                isLoading,
                onClickBack,

                result,
                setResult,
                rubricErrors,
                setRubricErrors,
                hasClickedSubmitRubric,
                setHasClickedSubmitRubric,
                validateTitle,
                validateCriteria,
                onClickEditSettings,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
